import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useSubscription } from '../../hooks/useSubscription';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { articles, decks, savedWords } = useAppContext();
  const { usedArticles, limits } = useSubscription();

  const dueThreshold = Date.now() + 1 * 60 * 60 * 1000;

  // Find due cards across all decks (due within 24h)
  const dueWords = savedWords.filter(w => (w.dueAt || 0) <= dueThreshold);
  const totalDue = dueWords.length;

  // Find deck with most due cards
  const deckDueCounts = decks.map(deck => ({
    deck,
    dueCount: savedWords.filter(w => w.deckId === deck.id && (w.dueAt || 0) <= dueThreshold).length,
    wordCount: savedWords.filter(w => w.deckId === deck.id).length,
  })).filter(d => d.dueCount > 0).sort((a, b) => b.dueCount - a.dueCount);

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  return (
    <div className="p-4 pt-14 md:p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{greeting}!</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Here's your study overview</p>
      </div>

      {/* Due cards call to action */}
      {totalDue > 0 ? (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-lg font-semibold text-primary-700 dark:text-primary-400">
                {totalDue} card{totalDue !== 1 ? 's' : ''} due for review
              </h2>
              <p className="text-sm text-primary-600/70 dark:text-primary-400/70 mt-1">
                Across {deckDueCounts.length} deck{deckDueCounts.length !== 1 ? 's' : ''}
              </p>
            </div>
            {deckDueCounts[0] && (
              <button
                onClick={() => navigate(`/app/decks/${deckDueCounts[0].deck.id}/review`)}
                className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors font-medium"
              >
                Review "{deckDueCounts[0].deck.name}" ({deckDueCounts[0].dueCount} due)
              </button>
            )}
          </div>
        </div>
      ) : savedWords.length > 0 || articles.length > 0 ? (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-emerald-700 dark:text-emerald-400">
            {savedWords.length > 0 ? 'All caught up!' : 'No words saved yet'}
          </h2>
          <p className="text-sm text-emerald-600/70 dark:text-emerald-400/70 mt-1">
            {savedWords.length > 0
              ? 'No cards due right now. Great work!'
              : 'Open an article and click words to save them to a deck for review.'}
          </p>
          {savedWords.length === 0 && (
            <button
              onClick={() => navigate(`/app/articles/${articles[0]?.id}`)}
              className="mt-3 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors font-medium"
            >
              Open an Article
            </button>
          )}
        </div>
      ) : (
        <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300">Get started</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Add your first article to start learning vocabulary!
          </p>
          <button
            onClick={() => navigate('/app/articles')}
            className="mt-3 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors font-medium"
          >
            Add an Article
          </button>
        </div>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{savedWords.length}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Words Saved</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{decks.length}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Decks</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{articles.length}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Articles</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">
            {usedArticles}/{limits.maxArticles === Infinity ? '∞' : limits.maxArticles}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Articles This Month</div>
        </div>
      </div>

      {/* Decks needing review */}
      {deckDueCounts.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-4">Decks to Review</h2>
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <ul className="divide-y divide-slate-100 dark:divide-slate-700">
              {deckDueCounts.map(({ deck, dueCount, wordCount }) => (
                <li key={deck.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{deck.name}</span>
                    <span className="text-xs text-slate-400 ml-2">{wordCount} words</span>
                  </div>
                  <button
                    onClick={() => navigate(`/app/decks/${deck.id}/review`)}
                    className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs rounded-md transition-colors font-medium"
                  >
                    Review ({dueCount} due)
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div>
        <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/app/articles')}
            className="flex items-center gap-3 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md hover:-translate-y-0.5 transition-all text-left"
          >
            <div className="w-10 h-10 bg-primary-50 dark:bg-primary-900/30 rounded-lg flex items-center justify-center text-primary-600 dark:text-primary-400">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-medium text-slate-800 dark:text-slate-100">Add New Article</div>
              <div className="text-xs text-slate-400">Parse Japanese text and learn vocabulary</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/app/decks')}
            className="flex items-center gap-3 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md hover:-translate-y-0.5 transition-all text-left"
          >
            <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L12 12.75 6.429 9.75m11.142 0l4.179 2.25-9.75 5.25-9.75-5.25 4.179-2.25" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-medium text-slate-800 dark:text-slate-100">Manage Decks</div>
              <div className="text-xs text-slate-400">View and organize your flashcard decks</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
